// React
import { useState, useEffect, useCallback, useRef } from 'react'

export const useCachedQuery = (queryFn, options = {}) => {
  const [isLoading, setIsLoading] = useState(false)

  // Cache to store results for different keys
  const cache = useRef(new Map())
  const previousKey = useRef(options.key)

  useEffect(() => {
    if (previousKey.current !== options.key) {
      // If we have cached data for the new key, use it
      if (options.key !== undefined && cache.current.has(options.key)) {
        options.onSuccess?.(cache.current.get(options.key))
      }

      previousKey.current = options.key
    }
  }, [options.key])

  const perform = useCallback(
    async (...args) => {
      // If key is undefined, always fetch
      // If key has changed from the previous fetch, fetch again
      // If key exists but no cached data, fetch
      const shouldFetch = options.key === undefined || !cache.current.has(options.key)

      if (!shouldFetch) {
        const cached = cache.current.get(options.key)

        options.onSuccess?.(cached)

        return cached
      }

      setIsLoading(true)

      options.onLoading?.()

      try {
        const result = await queryFn(...args)

        options.onSuccess?.(result)

        // Cache the result if we have a key
        if (options.key !== undefined) {
          cache.current.set(options.key, result)
        }

        return result
      } catch (err) {
        options.onError?.(err)

        throw new Error(options.errorKey || 'fetch_error')
      } finally {
        setIsLoading(false)
      }
    },
    [queryFn, options.key]
  )

  return {
    isLoading,
    perform
  }
}
